import React, { useEffect, useMemo, useRef } from 'react';
import { animated, useSpring, useSprings } from '@react-spring/web';
import { useInViewport } from '../../../utils/useIntersectionObserver';
import { calculateIn, calculateTopToBottomBottomToBottom } from '../../../utils/calcScrollPositions';
import { exponentialProgressEasing } from '../../../utils/exponentialEasing';
import { edgeToEdgeImageSizes } from '../../../utils/imageMediaQuerySizes';
import { mergeClassNames } from '../../../utils/mergeClassnames';
import { useRequestAnimFrameOnScroll } from '../../../context/onRequestAnimFrameOnScroll';
// import { useScrollSnap } from '../../../utils/useScrollSnap';
import RichText from '../../generic/RichText';
import AccordionScrollProgress, { DebugProgress } from '../../generic/AccordionScrollProgress';
import SVGIcon from '../../generic/SVGIcon';
import Image from '../../generic/Image';
import {
  Fixed,
  ScrollDistanceContainer,
  OpacityOnScroll,
  ScrollDistanceElem,
  TransitionFadeOnAnimationValue,
} from '../ScrollProgression';
import styles from './index.module.scss';

/**
 * Features component displaying the Workspace' 'Ways to collaborate' chapter
 * @param {Object} props props
 * @param {string} props.productTitle the product title for the header
 * @param {string} props.featuresHeading the title for the header
 * @param {Array<Object>} props.features content in a list of <Feature> from the CMS
 * @param {Object} props.defaultImage a default image to set size for element
 * @param {boolean} [props.infocardAlternatePos] pass in true to place infocard top right in the view
 * @param {boolean} [props.debug] draw out debug visuals to emphasize component borders etc.
 */
export default function Features({
  productTitle,
  featuresHeading,
  features,
  defaultImage,
  infocardAlternatePos = false,
  debug = false,
}) {
  let [firstFeature] = features;
  let { image: firstFeatureImage } = firstFeature;
  defaultImage = edgeToEdgeImageSizes(defaultImage ? defaultImage : firstFeatureImage);

  let intersectionRef = useRef(null);
  let inView = useInViewport(intersectionRef);
  let { registerCallback } = useRequestAnimFrameOnScroll();
  features = features ? features : [];
  // useScrollSnap();
  let featureSections = useMemo(() => new Map(), []); // stores feature image ids to be able to scroll to these via js (smooth scroll in safari)

  function getProgress() {
    let rect = intersectionRef.current?.getBoundingClientRect();
    let progress = rect ? calculateTopToBottomBottomToBottom(rect) : 0;
    return progress;
  }

  let [pointerEventsSpring, setPointerEventsSpring] = useSpring(() => ({ pointerEvents: 'none' })); //debug let totalProgress = getProgress(); return { totalProgress, scrollInProgress: totalProgress, pointerEvents: 'none' }; - for debug
  let [progressSprings, setProgressSprings] = useSprings(features.length, () => ({
    progress: getProgress(),
    fadeInOutProgress: getProgress(),
  }));

  useEffect(() => {
    let unregister = registerCallback(() => {
      let rect = intersectionRef.current?.getBoundingClientRect();
      let scrollInProgress = exponentialProgressEasing(calculateIn(rect));
      let totalProgress = getProgress();
      setPointerEventsSpring.start({ pointerEvents: scrollInProgress > 0.8 ? 'auto' : 'none', immediate: true }); //debug { totalProgress, scrollInProgress, pointerEvents: scrollInProgress > 0.8 ? 'auto' : 'none', immediate: true }
      setProgressSprings.start(index => {
        let progress = totalProgress * features.length - index;
        let fadeInOutProgress = mapProgressToFadeInOut(progress);
        return {
          progress: exponentialProgressEasing(progress),
          fadeInOutProgress: exponentialProgressEasing(fadeInOutProgress),
          immediate: true,
        };
      });
    });
    return () => unregister();
  });

  const accordionElements = useMemo(() => {
    return features.map(feature => {
      let { heading = null, id = null, image = {} } = feature;
      return {
        image,
        key: id,
        listElement: <FeaturesListTitle heading={heading} />,
        expandingElement: <FeaturesListDrawer {...feature} />,
      };
    });
  }, [features]);
  if (features.length < 1) return null;

  return (
    <>
      <ScrollDistanceContainer ref={intersectionRef} debug={debug} isHidden>
        {features.map(({ id }) => (
          <FeaturesScrollDistanceElem
            key={id}
            id={id}
            defaultImage={defaultImage}
            debug={debug}
            featureSections={featureSections}
          />
        ))}
      </ScrollDistanceContainer>
      <Fixed
        className={styles.features}
        breakOverlay
        constrainOverlay
        deactivateFixed={!inView}
        overlay={
          <OpacityOnScroll elemRef={intersectionRef} disableControlPointerEvents style={pointerEventsSpring}>
            <AccordionScrollProgress
              className={mergeClassNames(styles.listCard, infocardAlternatePos ? styles.altPos : styles.defaultPos)}
              elements={accordionElements}
              contentElems={featureSections}
              progressSprings={progressSprings}
              heading={featuresHeading ? featuresHeading : `Ways to collaborate with the Webex ${productTitle}`}
              debug={debug}
            />
          </OpacityOnScroll>
        }
      >
        <animated.div className={mergeClassNames(styles.imageContainer)} style={pointerEventsSpring}>
          {features.map((feature, index) => {
            const { progress } = progressSprings[index];
            return (
              <FeatureImage feature={feature} key={feature.id} motionValue={progress?.to(progressVal => progressVal)} />
            );
          })}
        </animated.div>
      </Fixed>
      <DebugProgress
        heading={`<${Features.name}>`}
        progresses={[...progressSprings, pointerEventsSpring]}
        debug={debug}
      />
    </>
  );
}

export function mapProgressToFadeInOut(value) {
  if (value >= 1) return 2 - value;
  return value;
}

function FeaturesScrollDistanceElem({ defaultImage, id, debug = false, featureSections }) {
  const ref = useRef(null);
  useEffect(() => {
    featureSections.set(id, ref);
    return () => featureSections.delete(id);
  }, [featureSections, id]);

  return (
    <ScrollDistanceElem
      ref={ref}
      image={defaultImage}
      isVisible
      isFullHeight
      // snapScroll
      debug={debug}
    />
  );
}

function FeaturesListDrawer({ description, icon }) {
  description = description?.hasOwnProperty('description') ? description.description : '';

  return (
    <div className={styles.rowContent}>
      <SVGIcon svgIcon={icon} className={styles.icon} />
      <RichText className={styles.paragraph}>{description}</RichText>
    </div>
  );
}

function FeaturesListTitle({ heading }) {
  return <p className={styles.rowTitle}>{heading}</p>;
}

function FeatureImage({ feature, motionValue }) {
  const image = edgeToEdgeImageSizes(feature.image);
  return (
    <TransitionFadeOnAnimationValue key={feature.id} className={styles.featureImage} motionValue={motionValue}>
      {Boolean(feature?.image) && <Image fluid={image.fluid} alt={image.description} />}
    </TransitionFadeOnAnimationValue>
  );
}
